import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type { TCheckResult } from '@conversed/rest/src/contracts/llm-usages/check-results';
import { useLLMUsagesActions } from '@hooks/llmUsages/useLLMUsagesActions';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Props {
  selectedWeeks: number[];
  selectedConfigId: string;
  year: number;
  llmBlockId: string;
  totalCheckedItems: number;
  totalItems: number;
  updatedConversation?: (response: TCheckResult) => void;
  callback?: () => void;
  requestedHotTopicId?: string;
}

export const FactCheckingPrestartModal: React.FC<Props> = ({
  selectedWeeks,
  selectedConfigId,
  year,
  llmBlockId,
  totalCheckedItems,
  totalItems,
  updatedConversation,
  callback,
  requestedHotTopicId,
}) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const onlyAllOptionIsAvailable = totalCheckedItems === totalItems;
  // Default to only unverified answers if not all items are checked
  const [onlyUnverifiedAnswers, setOnlyUnverifiedAnswers] = useState(
    !onlyAllOptionIsAvailable,
  );
  const { getConversationsByWeeks } = useLLMUsagesActions();

  const handleStart = useCallback(async () => {
    hideModal();

    const data = await getConversationsByWeeks(
      selectedWeeks,
      selectedConfigId,
      year,
      llmBlockId,
      onlyUnverifiedAnswers,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      requestedHotTopicId,
    );

    showModal('train-ai-fact-checking', '', {
      data,
      updatedConversation,
      callback,
      llmBlockId,
      requestedHotTopicId,
    });
  }, [
    selectedWeeks,
    selectedConfigId,
    getConversationsByWeeks,
    hideModal,
    showModal,
    onlyUnverifiedAnswers,
    callback,
    updatedConversation,
    year,
    llmBlockId,
    requestedHotTopicId,
  ]);

  return (
    <form className='tailwind-modal-form flex-1 min-h-[220px] px-2'>
      <div className='tailwind-modal-body flex flex-col gap-3 h-inherit border border-primary-base rounded-md py-4 px-2 bg-gray-100'>
        <div className='flex flex-col text-base text-primary-base'>
          <span>{t('train-ai.fact-checking.prestart.desc')}</span>
          <span className='font-medium'>
            {t('train-ai.fact-checking.prestart.desc2')}
          </span>
        </div>

        <div className='flex flex-col gap-5 py-4 px-6 select-none'>
          <div
            className={`flex gap-2 items-center ${onlyAllOptionIsAvailable ? 'cursor-default' : 'cursor-pointer'}`}>
            <InputT
              id='only-unverified-ai-answer'
              type='radio'
              parentClassName='!mb-0'
              checked={onlyUnverifiedAnswers}
              options={{
                onChange: () => setOnlyUnverifiedAnswers(true),
              }}
              disabled={onlyAllOptionIsAvailable}
            />
            <LabelT
              htmlFor='only-unverified-ai-answer'
              text={t('train-ai.fact-checking.prestart.option1')}
              className={`text-[16px] font-medium ${onlyAllOptionIsAvailable ? '!text-gray-200 line-through' : 'text-primary-base'}`}
            />
          </div>
          <div className='flex gap-2 items-center cursor-pointer'>
            <InputT
              id='all-ai-answers'
              type='radio'
              parentClassName='!mb-0'
              checked={!onlyUnverifiedAnswers}
              options={{
                onChange: () => setOnlyUnverifiedAnswers(false),
              }}
            />
            <LabelT
              htmlFor='all-ai-answers'
              text={t('train-ai.fact-checking.prestart.option2')}
              subText={t('train-ai.fact-checking.prestart.option2-subText')}
              subTextClassName='text-base font-normal'
              className='text-[16px] font-medium text-primary-base'
            />
          </div>
        </div>
        {onlyAllOptionIsAvailable && (
          <div className='flex text-red-base text-sm justify-center flex-wrap'>
            <span className='flex w-full'>
              {t('train-ai.fact-checking.prestart.all-answered-warning-1')}
            </span>
            <span className='flex w-full'>
              {t('train-ai.fact-checking.prestart.all-answered-warning-2')}
            </span>
          </div>
        )}
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleStart}>
          {t('general.buttons.start')}
        </ButtonT>
      </div>
    </form>
  );
};

export default FactCheckingPrestartModal;
